<script setup lang="ts">
import { MContainer, MContainerColumn, MContainerRow, MDialogConfirmButtonRedesign, MDialogCancelButtonRedesign } from "@xlabmedical/medic-web-framework";
import type { DynamicDialogOptions } from "primevue/dynamicdialogoptions";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { getOrigin } from "@/constants/GlobalConstants";

enum PrivacyType {
    PRIVACY = "privacy",
    EULA = "eula",
}

const { t } = useI18n();

const dialogRef = inject("dialogRef") as DynamicDialogOptions;

const params = dialogRef.value.data as {
    isFileShare?: boolean;
};

const message = ref("");
const username = ref("");
const usernameTouched = ref(false);
const editable = ref(true);
const anonymous = ref(false);
const analogHoles = ref(false);
const acceptedPrivacy = ref(false);

const documentType = ref(PrivacyType.PRIVACY);

const emits = defineEmits(["shareProject", "shareProjectFile"]);

const shareProjectClick = () => {
    emits("shareProject", message.value, username.value, editable.value, anonymous.value);

    dialogRef.value.close();
};

const shareProjectFileClick = () => {
    emits("shareProjectFile", message.value, username.value, editable.value, anonymous.value, analogHoles.value);

    dialogRef.value.close();
};

const rawText = computed(() => t("web-rg.bySharingTheProjectYouExplicitlyAcceptThePrivacyPolicyAndTheEula"));

const assetUrl = computed(() => {
    const origin = getOrigin().includes("localhost") ? "https://beta-cloud.medicteam.io" : getOrigin();

    return `${origin}/auth/asset/${documentType.value}`;
});

const isUsernameValid = computed(() => username.value.trim().length > 0);

async function openPrivacyDialog() {
    documentType.value = PrivacyType.PRIVACY;

    if (typeof window !== "undefined") {
        window.open(assetUrl.value, "_blank");
    }
}

function openEULADialog() {
    documentType.value = PrivacyType.EULA;

    if (typeof window !== "undefined") {
        window.open(assetUrl.value, "_blank");
    }
}

const labelSegments = computed(() => {
    const privacy = t("weblink.privacyPolicy");
    const eula = "EULA";

    const [beforePrivacy, afterPrivacy] = rawText.value.split(privacy);
    if (afterPrivacy === undefined) {
        return null;
    }

    const [betweenPrivacyAndEula, afterEula] = afterPrivacy.split(eula);
    if (afterEula === undefined) {
        return null;
    }

    return {
        beforePrivacy,
        betweenPrivacyAndEula,
        afterEula,
        privacy,
        eula,
    };
});
</script>

<template>
    <MContainerColumn class="gap-8">
        <MContainer class="flex-col w-full">
            <label for="value">{{ $t("shared.message") }}</label>
            <InputText id="shareProjectMessage" class="w-full mt-2" v-model="message" type="text" />
        </MContainer>
        <MContainer class="flex-col w-full">
            <label for="value">{{ $t("web-rg.withUserEmail") }}</label>
            <InputText id="shareProjectMessage" :class="['w-full mt-2', { 'p-error-input': !isUsernameValid }]" v-model="username" type="text" @blur="usernameTouched = true" />
            <small v-if="!isUsernameValid" class="p-error pt-1">{{ `${$t("web-rg.withUserEmail")} ${$t("shared.isRequired")}` }}</small>
        </MContainer>
        <MContainerColumn class="gap-6">
            <MContainerRow class="gap-3 items-center">
                <Checkbox v-model="anonymous" inputId="shareProjectAnonymous" name="shareProjectAnonymous" binary />
                <label for="shareProjectAnonymous">{{ $t("web-rg.anonymize") }}</label>
            </MContainerRow>
            <MContainerRow class="gap-3 items-center">
                <Checkbox v-model="editable" inputId="shareProjectEditable" name="shareProjectEditable" binary />
                <label for="shareProjectEditable">{{ $t("web-rg.editable") }}</label>
            </MContainerRow>
            <MContainer class="flex-row gap-3 items-center">
                <Checkbox v-model="acceptedPrivacy" inputId="acceptPrivacy" name="acceptPrivacy" binary />
                <span>
                    <template v-if="labelSegments">
                        {{ labelSegments.beforePrivacy }}
                        <span class="clickable-text privacy-link" @click="openPrivacyDialog">{{ labelSegments.privacy }}</span>
                        {{ labelSegments.betweenPrivacyAndEula }}
                        <span class="clickable-text eula-link" @click="openEULADialog">{{ labelSegments.eula }}</span>
                        {{ labelSegments.afterEula }}
                        <span class="p-error">*</span>
                    </template>
                    <template v-else>
                        {{ rawText }}
                    </template>
                </span>
            </MContainer>
        </MContainerColumn>
        <Divider />
        <MContainerRow class="gap-4 justify-center -mt-2">
            <MDialogConfirmButtonRedesign
                v-if="params.isFileShare"
                id="editPatientDialogSavePatient"
                v-bind:label="$t('shared.share')"
                :icon="'pi pi-check'"
                :disabled="!isUsernameValid || !acceptedPrivacy"
                v-on:click="shareProjectFileClick" />
            <MDialogConfirmButtonRedesign
                v-else
                id="editPatientDialogSavePatient"
                v-bind:label="$t('shared.share')"
                :icon="'pi pi-check'"
                :disabled="!isUsernameValid || !acceptedPrivacy"
                v-on:click="shareProjectClick" />
            <MDialogCancelButtonRedesign v-bind:label="$t('shared.cancel')" :icon="'pi pi-times'" v-on:click="dialogRef.close()" />
        </MContainerRow>
    </MContainerColumn>
</template>

<style scoped>
:deep(.clickable-text) {
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
}
</style>
